import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import { StatusService } from "../../../services/user/statuses.services";
import { handleNotify, TOASTER_TYPE, TOASTER_POSITION } from "../../common/notification/toaster_notify.component";
import { useSelector } from "react-redux";
import Select from 'react-select';

const AddStatusComponent = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const statusId = location.state?.statusId || 0;
    const isEditMode = !!statusId;
    const selectedStore = useSelector((state) => state?.user?.selectedStoreId);
    const [loading, setLoading] = useState(false);

    const statusTypeOptions = [
        { value: 'Invoice', label: 'Invoice' },
        { value: 'Quotes', label: 'Quotes' },
        { value: 'Leads', label: 'Leads' },
        { value: 'Activity', label: 'Activity' },
    ];

    const statusColorOptions = [
        { value: 0, label: 'Blue' },
        { value: 1, label: 'Teal' },
        { value: 2, label: 'Green' },
        { value: 3, label: 'Pink' },
        { value: 4, label: 'Red' },
        { value: 5, label: 'Gold' },
        { value: 6, label: 'Purple' },
        { value: 7, label: 'Orange' },
        { value: 8, label: 'Yellow' },
    ];

    const formik = useFormik({
        initialValues: {
            statusId: 0,
            statusName: "",
            statustype: statusTypeOptions[0].value,
            colorId: 0,
            isActive: true,
            storeId: selectedStore,
        },
        validationSchema: Yup.object({
            statusName: Yup.string().required("Please enter a valid name"),
        }),
        onSubmit: async (values) => {
            try {
                setLoading(true);
                const colorIdAsNumber = Number(values.colorId);
                const data = {
                    ...values,
                    colorId: colorIdAsNumber,
                    isActive: values.isActive === true ? true : false
                };
                if (isEditMode) {
                    await StatusService.updateStatusById(statusId, data).then((response) => {
                        if (response) {
                            navigate("/statuses");
                        }
                    });
                } else {
                    await StatusService.addNewStatus(data).then((response) => {
                        if (response) {
                            navigate("/statuses");
                        }
                    });
                }
            } catch (error) {
                console.error("Error while submitting form:", error);
                handleNotify(
                    "An error occurred while saving the status.",
                    TOASTER_TYPE.ERROR,
                    TOASTER_POSITION.TOP_RIGHT
                );
            } finally {
                setLoading(false);
            }
        },
    });

    useEffect(() => {
        if (isEditMode) {
            const fetchStatusDetail = async () => {
                try {
                    const data = await StatusService.getStatusById(statusId)
                    formik.setValues({
                        statusName: data?.statusName,
                        statustype: data?.statustype,
                        colorId: data?.colorId,
                        isActive: data?.isActive,
                    })
                }
                catch (error) {
                    console.log("Error fetching Status details", error)
                }
            }
            fetchStatusDetail()
        }
    }, [])


    const handleBackClick = () => {
        navigate("/statuses");
    };

    return (
        <div className="page-wrapper m-0">
            <div className="page-content">
                <div className="page-breadcrumb d-flex align-items-center mb-3">
                    <div
                        className="breadcrumb-title pe-3 text-primary fw-bold fs-4 cursor-pointer"
                        onClick={handleBackClick}
                    >
                        Statuses</div>
                    <div className="ps-3">
                        <nav aria-label="breadcrumb">
                            <ol className="breadcrumb mb-0 p-0">
                                <li className="breadcrumb-item active" aria-current="page">
                                    {isEditMode ? "Edit Status" : "New Status"}
                                </li>
                            </ol>
                        </nav>
                    </div>
                    <div className="ms-auto">
                        <button
                            type="button"
                            className="btn btn-outline-secondary me-2 px-4 py-2 rounded-3 shadow-sm hover-effect"
                            onClick={handleBackClick}
                        >
                            Back
                        </button>
                    </div>
                </div>

                <div className="card radius-10 border-top border-0 border-4 border-primary p-4">
                    <div className="card-body form-container">
                        <form onSubmit={formik.handleSubmit} className="mt-4">
                            {/* Name Field */}
                            <div className="row mb-4">
                                <div className="col-md-6 col-12">
                                    <label htmlFor="statusName" className="form-label d-flex">
                                        Name <span className="text-danger">*</span>:
                                    </label>
                                    <input
                                        type="text"
                                        id="statusName"
                                        name="statusName"
                                        className={`form-control ${formik.touched.statusName && formik.errors.statusName ? "is-invalid" : ""}`}
                                        placeholder="Enter Status Name"
                                        value={formik.values.statusName}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                    />
                                    {formik.touched.statusName && formik.errors.statusName && (
                                        <div className="invalid-feedback d-flex">{formik.errors.statusName}</div>
                                    )}
                                </div>
                            </div>

                            {/* Type Field (Dropdown) */}
                            <div className="row mb-4">
                                <div className="col-md-6 col-12">
                                    <label htmlFor="statustype" className="form-label d-flex">
                                        Type:
                                    </label>
                                    <div className="text-start">
                                        <Select
                                            id="statustype"
                                            name="statustype"
                                            options={statusTypeOptions}
                                            value={statusTypeOptions.find(option => option.value === formik.values.statustype)}
                                            onChange={selectedOption => formik.setFieldValue('statustype', selectedOption.value)}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Color Field (Dropdown) */}
                            <div className="row mb-4">
                                <div className="col-md-6 col-12">
                                    <label htmlFor="colorId" className="form-label d-flex">
                                        Color:
                                    </label>
                                    <div className="text-start">
                                        <Select
                                            id="colorId"
                                            name="colorId"
                                            options={statusColorOptions}
                                            value={statusColorOptions.find(option => option.value === formik.values.colorId)}
                                            onChange={selectedOption => formik.setFieldValue('colorId', selectedOption.value)}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Status Radio Buttons */}
                            <div className="d-flex align-items-center mb-4">
                                <div className="form-check form-check-inline me-3">
                                    <input
                                        type="radio"
                                        id="active"
                                        name="isActive"
                                        className="form-check-input"
                                        value={true}
                                        onChange={() => formik.setFieldValue("isActive", true)}
                                        onBlur={formik.handleBlur}
                                        checked={formik.values.isActive === true}
                                    />
                                    <label className="form-check-label" htmlFor="active">
                                        Active
                                    </label>
                                </div>
                                <div className="form-check form-check-inline">
                                    <input
                                        type="radio"
                                        id="inactive"
                                        name="isActive"
                                        className="form-check-input"
                                        value={false}
                                        onChange={() => formik.setFieldValue("isActive", false)}
                                        onBlur={formik.handleBlur}
                                        checked={formik.values.isActive === false}
                                    />
                                    <label className="form-check-label" htmlFor="inactive">
                                        Inactive
                                    </label>
                                </div>
                            </div>

                            {/* Submit */}
                            <hr />
                            <div className="d-flex flex-column flex-md-row mt-4 justify-content-end">
                                <button
                                    type="button"
                                    className="btn btn-outline-secondary mb-2 mb-md-0 me-2 px-4 py-2 rounded-3 shadow-sm hover-effect"
                                    onClick={handleBackClick}
                                >
                                    Back
                                </button>
                                <button
                                    type="submit"
                                    className="btn btn-primary px-4 py-2 rounded-3 shadow-sm hover-effect"
                                    disabled={loading}
                                >
                                    {loading ? "Saving..." : isEditMode ? "Update" : "Save"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AddStatusComponent;
